/**
 * Recording Mode Selector Component
 *
 * Allows users to choose between uploading an existing video
 * or recording with guided assistance.
 */

import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Box
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { OndemandVideo, Videocam } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    maxWidth: 600,
    margin: '0 auto'
  },
  title: {
    marginBottom: theme.spacing(3),
    textAlign: 'center',
    fontWeight: 600
  },
  card: {
    height: '100%',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8]
    }
  },
  cardContent: {
    textAlign: 'center',
    padding: theme.spacing(3)
  },
  icon: {
    fontSize: 64,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2)
  },
  optionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  optionDescription: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem'
  }
}));

export type RecordingMode = 'upload' | 'record';

interface RecordingModeSelectorProps {
  onModeSelected: (mode: RecordingMode) => void;
  guidedRecordingSupported?: boolean;
}

export default function RecordingModeSelector({
  onModeSelected,
  guidedRecordingSupported = true
}: RecordingModeSelectorProps): JSX.Element {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.container}>
      <Typography variant="h5" className={classes.title}>
        {t('modeselector.title', {ns: 'guidedrecording'})}
      </Typography>

      <Grid container spacing={3}>
        {/* Upload Existing Video Option */}
        <Grid item xs={12} sm={6}>
          <Card className={classes.card}>
            <CardActionArea onClick={() => onModeSelected('upload')}>
              <CardContent className={classes.cardContent}>
                <OndemandVideo className={classes.icon} />
                <Typography variant="h6" className={classes.optionTitle}>
                  {t('modeselector.upload', {ns: 'guidedrecording'})}
                </Typography>
                <Typography className={classes.optionDescription}>
                  {t('modeselector.uploaddesc', {ns: 'guidedrecording'})}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        {/* Record with Guidance Option */}
        <Grid item xs={12} sm={6}>
          <Card className={classes.card}>
            <CardActionArea
              onClick={() => onModeSelected('record')}
              disabled={!guidedRecordingSupported}
            >
              <CardContent className={classes.cardContent}>
                <Videocam className={classes.icon} />
                <Typography variant="h6" className={classes.optionTitle}>
                  {t('modeselector.record', {ns: 'guidedrecording'})}
                </Typography>
                <Typography className={classes.optionDescription}>
                  {t('modeselector.recorddesc', {ns: 'guidedrecording'})}
                </Typography>
                {!guidedRecordingSupported && (
                  <Typography
                    color="error"
                    variant="caption"
                    style={{ marginTop: 8, display: 'block' }}
                  >
                    {t('errors.unsupported', {ns: 'guidedrecording'})}
                  </Typography>
                )}
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
